import { useMemo } from 'react';
import { GroupedOptionsConfig } from 'src/core/POS/PosMultiSelect/PosMultiSelect.types';
import { ContentId } from 'src/utils/constants/contentId';
import {
  PARENT_CATEGORY_TO_CID,
  STUBHUB_PARENT_CATEGORY_GROUPS,
  STUBHUB_PARENT_CATEGORY_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { Feature } from 'src/WebApiController';

import { useUserHasFeature } from './useUserHasFeature';

export type ParentCategoryToCidResult = {
  /** The flat map of category IDs to ContentIds */
  map: Record<string, ContentId>;
  /** Optional grouped configuration for displaying categories in sections */
  groupedConfig?: GroupedOptionsConfig;
};

/**
 * Returns the appropriate parent category to ContentId map based on the StubHubSubGenresInPOS feature flag.
 *
 * When the feature flag is enabled, returns STUBHUB_PARENT_CATEGORY_TO_CID which:
 * - Replaces generic sport types (Baseball, Basketball, American Football) with league-specific categories (MLB, NBA, NFL)
 * - Adds new categories: NHL, MLS, Tennis, F1, Rugby, Horse Racing, Cricket, WWE, Volleyball
 * - Adds new concert categories: Pop, Heavy Metal, Funk, Experimental
 * - Includes grouped configuration for sectioned dropdown display
 *
 * When the feature flag is disabled, returns the legacy PARENT_CATEGORY_TO_CID without grouping.
 */
export const useParentCategoryToCid = (): ParentCategoryToCidResult => {
  const hasStubHubSubGenresFeature = useUserHasFeature(
    Feature.StubHubSubGenresInPOS
  );

  return useMemo(
    () =>
      hasStubHubSubGenresFeature
        ? {
            map: STUBHUB_PARENT_CATEGORY_TO_CID,
            groupedConfig: STUBHUB_PARENT_CATEGORY_GROUPS,
          }
        : {
            map: PARENT_CATEGORY_TO_CID,
            groupedConfig: undefined,
          },
    [hasStubHubSubGenresFeature]
  );
};
